import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import useSidebarCollapsed from '../hooks/useSidebarCollapsed';
import CalendarWidget from '../components/CalendarWidget';
import HolidayForm from '../components/HolidayForm';
import holidayAPI from '../api/holiday';
import { attendanceAPI, default as api } from '../api/client';
import { AuthContext } from '../context/AuthContext';
import { format, isToday, isSameMonth } from 'date-fns';
import { FiMapPin, FiClock, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { usePermissions } from '../hooks/usePermissions';
import { leaveAPI } from "../api/client";
import useRealtimeData from '../hooks/useRealtimeData';


// Geo-fence configuration for attendance marking
const ALLOWED_RADIUS_METERS = 100;
const OFFICE_LOCATION = {
  lat: 28.5105188863241,
  lng: 77.38054726639055
};

const LiveClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="text-center p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg text-white">
      <div className="text-3xl font-bold">{format(time, 'HH:mm:ss')}</div>
      <div className="text-xs mt-1 opacity-90">{format(time, 'EEEE, MMM dd, yyyy')}</div>
    </div>
  );
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default function AttendancePage({ newMessageCount = 0, resetNewMessageCount }) {
    const sidebarCollapsed = useSidebarCollapsed();
    // Holidays state for calendar
    const [holidays, setHolidays] = useState([]);
    // Show/hide holiday form
    const [showHolidayForm, setShowHolidayForm] = useState(false);

    useEffect(() => {
      fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
      try {
        const res = await holidayAPI.getAll();
        setHolidays(res.data || []);
      } catch (err) {
        setHolidays([]);
      }
    };
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const canViewTeamAttendance =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    (user?.role === 'EMPLOYEE' && (user?.teamAttendanceAccess || user?.permissions?.includes('Attendance Insights')));
  const { canViewAttendance, canMarkAttendance, loading: permissionsLoading } = usePermissions();
  const [attendance, setAttendance] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [distanceFromOffice, setDistanceFromOffice] = useState(null);
  const [withinRadius, setWithinRadius] = useState(false);
  const [checkedInToday, setCheckedInToday] = useState(false);
  const [checkedOutToday, setCheckedOutToday] = useState(false);
  const [isAfterNoon, setIsAfterNoon] = useState(new Date().getHours() >= 12);
  const [markedLocation, setMarkedLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  
  // Geo-fence specific states
  const [isCheckingLocation, setIsCheckingLocation] = useState(true);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);
  const [isWithinOfficeGeofence, setIsWithinOfficeGeofence] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'sick'
  });
  
  // Manager-specific states
  const [employees, setEmployees] = useState([]);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'monthly'
  const [selectedEmployeeTimeline, setSelectedEmployeeTimeline] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  // Employee-specific states
  const [employeeViewMode, setEmployeeViewMode] = useState('current'); // 'current' or 'history'
  const [employeeSelectedMonth, setEmployeeSelectedMonth] = useState(new Date());
  const [myJoiningDate, setMyJoiningDate] = useState(null); // Track when employee joined
  
  // Reload team attendance when selected month changes (for both daily & monthly views)
  useEffect(() => {
    if (user && canViewTeamAttendance) {
      console.log('📅 Loading attendance for month:', format(selectedMonth, 'MMMM yyyy'));
      loadTeamAttendance();
    }
  }, [selectedMonth, viewMode]);


  useEffect(() => {
    loadAttendance();
    loadLeaveRequests();
    checkUserLocation();
    checkTodayAttendance();
    
    // Fetch this employee's joining date for accurate absent calculation
    if (user?.employeeId) {
      api.get(`/employees/${user.employeeId}`)
        .then(res => {
          const emp = res.data?.data || res.data;
          setMyJoiningDate(emp?.joiningDate || emp?.createdAt || null);
        })
        .catch(() => {});
    }
    
    // Load manager-specific data
    if (user && canViewTeamAttendance) {
      loadEmployees();
      loadTeamAttendance();
    }
    
    // Poll every 30 seconds for real-time updates
    const interval = setInterval(() => {
      loadAttendance();
      loadLeaveRequests();
      checkTodayAttendance();
      if (user && canViewTeamAttendance) {
        loadTeamAttendance();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);
  
  // Real-time team attendance updates via shared socket
  const refreshAttendance = useCallback(() => {
    loadAttendance();
    loadLeaveRequests();
    checkTodayAttendance();
    if (user && canViewTeamAttendance) loadTeamAttendance();
  }, [user]);
  useRealtimeData(['attendance-updated', 'team-attendance-updated'], refreshAttendance);

  // Update isAfterNoon every minute to auto-enable checkout button at 12PM
  useEffect(() => {
    const noonTimer = setInterval(() => {
      setIsAfterNoon(new Date().getHours() >= 12);
    }, 60000);
    return () => clearInterval(noonTimer);
  }, []);

  useEffect(() => {
    if (!permissionsLoading && !canViewAttendance) {
      navigate('/');
    }
  }, [canViewAttendance, permissionsLoading, navigate]);

  // Geo-fence: Check location on page load and setup polling
  useEffect(() => {
    // Initial location check
    checkGeofenceLocation();

    // Set up polling every 20 seconds to refresh location
    const locationPollingInterval = setInterval(() => {
      checkGeofenceLocation();
    }, 20000); // 20 seconds

    // Cleanup interval on unmount
    return () => {
      clearInterval(locationPollingInterval);
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const checkTodayAttendance = async () => {
    try {
      const response = await attendanceAPI.getHistory();
      const today = response.data.find(a => isToday(new Date(a.date)));
      if (today && (today.check_in || today.checkIn)) setCheckedInToday(true);
      if (today && (today.check_out || today.checkOut)) setCheckedOutToday(true);
      setIsAfterNoon(new Date().getHours() >= 12);
    } catch (err) {
      console.error('Failed to check today attendance:', err);
    }
  };

  // Geo-fence: Check if user is within office premises
  const checkGeofenceLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      setLocationPermissionDenied(true);
      setIsCheckingLocation(false);
      setIsWithinOfficeGeofence(false);
      return;
    }

    setIsCheckingLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        
        // Calculate distance from office using Haversine formula
        const distance = calculateDistance(
          latitude,
          longitude,
          OFFICE_LOCATION.lat,
          OFFICE_LOCATION.lng
        );
        
        setDistanceFromOffice(Math.round(distance));
        
        // Check if within allowed radius
        if (distance <= ALLOWED_RADIUS_METERS) {
          setIsWithinOfficeGeofence(true);
          setWithinRadius(true);
          setLocationError('');
          setLocationPermissionDenied(false);
        } else {
          setIsWithinOfficeGeofence(false);
          setWithinRadius(false);
          setLocationError(`You are ${Math.round(distance)}m away from office. Must be within ${ALLOWED_RADIUS_METERS}m.`);
          setLocationPermissionDenied(false);
        }
        
        setIsCheckingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsCheckingLocation(false);
        setLocationPermissionDenied(true);
        setIsWithinOfficeGeofence(false);
        
        // Handle different error types
        if (error.code === 1) {
          setLocationError('Location access required to mark attendance');
        } else if (error.code === 2) {
          setLocationError('Unable to determine your location');
        } else if (error.code === 3) {
          setLocationError('Location request timed out');
        } else {
          setLocationError('Error accessing location services');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const checkUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        if (markedLocation) {
          const distance = calculateDistance(latitude, longitude, markedLocation.lat, markedLocation.lng);
          if (distance > ALLOWED_RADIUS_METERS) {
            setLocationError(`You are ${Math.round(distance)}m away from marked location`);
          } else {
            setLocationError('');
          }
        } else {
          setLocationError('');
        }
      },
      (error) => {
        setLocationError('Enable location services');
        console.error('Geolocation error:', error);
      }
    );
  };

  const handleMark = async () => {
    if (!canMarkAttendance) {
      alert('You do not have permission to mark attendance.');
      return;
    }
    // Zone check now handled by canMarkAttendance logic

    setLoading(true);
    try {
      await attendanceAPI.checkIn({
        lat: userLocation?.lat,
        lng: userLocation?.lng
      });
      setMarkedLocation({ lat: userLocation?.lat, lng: userLocation?.lng });
      setCheckedInToday(true);
      await loadAttendance();
      alert('✓ Attendance marked successfully!');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to mark attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!canMarkAttendance) {
      alert('You do not have permission.');
      return;
    }
    setLoading(true);
    try {
      await attendanceAPI.checkOut();
      setCheckedOutToday(true);
      await loadAttendance();
      alert('✓ Checked out successfully!');
    } catch (err) {
      alert(err?.response?.data?.message || err?.response?.data?.error || 'Failed to check out');
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async () => {
    try {
      const response = await attendanceAPI.getHistory();
      // Map backend fields to expected frontend fields, sorted newest first
      const mapped = (response.data || [])
        .map((a) => ({
          ...a,
          check_in: a.check_in || a.checkIn,
          check_out: a.check_out || a.checkOut,
          date: a.date,
          status: (a.status || '').toLowerCase(),
        }))
        .sort((a, b) => new Date(b.date) - new Date(a.date));
      setAttendance(mapped);
    } catch (err) {
      console.error('Failed to load attendance:', err);
    }
  };

  const loadLeaveRequests = async () => {
    try {
      const response = await api.get('/leaves/my');
      // Map backend fields to expected frontend fields
      const mapped = (response.data || []).map((l) => ({
        ...l,
        start_date: l.fromDate,
        end_date: l.toDate,
        leave_type: l.leaveType,
        status: (l.status || '').toLowerCase(),
        reason: l.reason || '',
      }));
      setLeaveRequests(mapped);
    } catch (err) {
      console.error('Failed to load leave requests:', err);
    }
  };
  
  // Manager-specific data loading
  const loadEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };
  
  const loadTeamAttendance = async () => {
    try {
      const response = await api.get('/attendance/team');
      console.log('📊 Team attendance loaded:', response.data);
      setTeamAttendance(response.data || []);
    } catch (err) {
      console.error('Failed to load team attendance:', err);
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      alert('You are not logged in. Please log in to submit a leave request.');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      // Prepare payload for backend
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason,
        leave_type: formData.leave_type
      };
      await leaveAPI.requestLeave(payload);
      setShowLeaveForm(false);
      setFormData({ start_date: '', end_date: '', reason: '', leave_type: 'sick' });
      await loadLeaveRequests();
      alert('✓ Leave request submitted');
    } catch (err) {
      // Improved error logging for debugging
      console.error('Leave request error:', err?.response || err);
      alert(err?.response?.data?.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  // Find today's attendance record
  const todayRecord = attendance.find(a => a.date && isToday(new Date(a.date)));
  const todayCheckIn = (todayRecord && todayRecord.check_in && !isNaN(new Date(todayRecord.check_in)))
    ? format(new Date(todayRecord.check_in), 'HH:mm')
    : '-';
  const todayCheckOut = (todayRecord && (todayRecord.check_out || todayRecord.checkOut) && !isNaN(new Date(todayRecord.check_out || todayRecord.checkOut)))
    ? format(new Date(todayRecord.check_out || todayRecord.checkOut), 'HH:mm')
    : '-';

  // Calculate attendance status with business rules
  const getAttendanceStatus = (record) => {
    const recordDate = new Date(record.date);
    const dayOfWeek = recordDate.getDay(); // 0=Sunday, 1=Monday, etc.
    
    // Check if it's a Monday (weekly off)
    if (dayOfWeek === 1) return 'WO';
    
    // Check if it's a holiday
    const isHoliday = holidays.some(h => {
      const holidayDate = new Date(h.date);
      return holidayDate.toDateString() === recordDate.toDateString();
    });
    if (isHoliday) return 'H';
    
    // Check if there's an approved leave
    const hasApprovedLeave = leaveRequests.some(l => {
      if (l.status !== 'approved') return false;
      const startDate = new Date(l.start_date);
      const endDate = new Date(l.end_date);
      return recordDate >= startDate && recordDate <= endDate;
    });
    if (hasApprovedLeave) return 'L';
    
    // Check if there's a check-in (support both camelCase and snake_case)
    if (record.checkIn || record.check_in) return 'P';
    
    // Check if status field exists (database might have direct status field)
    if (record.status === 'PRESENT') return 'P';
    
    // Otherwise absent
    return 'A';
  };

  const presentCount = attendance.filter(a => {
    const status = getAttendanceStatus(a);
    return status === 'P';
  }).length;
  
  // Calculate absent by counting working days with no attendance in current month
  // Only counts from the employee's joining date (not from day 1 if joined mid-month)
  const absentCount = (() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const today = now.getDate();
    
    // Determine the start day — if employee joined this month, start from joining day
    let startDay = 1;
    if (myJoiningDate) {
      const jd = new Date(myJoiningDate);
      if (jd.getFullYear() === year && jd.getMonth() === month) {
        // Joined this month — only count from joining day
        startDay = jd.getDate();
      } else if (jd > now) {
        // Joining date is in the future — no absences
        return 0;
      }
      // If joined in a previous month, start from day 1 (default)
    }
    
    // Build a Set of dates (day number) that have any attendance record
    const attendedDays = new Set();
    attendance.forEach(a => {
      const d = new Date(a.date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const status = getAttendanceStatus(a);
        if (status === 'P' || status === 'L' || status === 'WO' || status === 'H') attendedDays.add(d.getDate());
      }
    });
    
    // Build a Set of holiday dates for current month
    const holidayDays = new Set();
    (holidays || []).forEach(h => {
      const d = new Date(h.date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        holidayDays.add(d.getDate());
      }
    });
    
    // Build a Set of approved leave dates for current month
    const leaveDays = new Set();
    leaveRequests.filter(l => l.status === 'approved').forEach(l => {
      const start = new Date(l.start_date || l.fromDate);
      const end = new Date(l.end_date || l.toDate);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year && d.getMonth() === month) {
          leaveDays.add(d.getDate());
        }
      }
    });
    
    let absent = 0;
    for (let day = startDay; day < today; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay();
      
      // Skip only Monday (1 = weekly off)
      if (dayOfWeek === 1) continue;
      
      // Skip holidays
      if (holidayDays.has(day)) continue;
      
      // Skip if on approved leave
      if (leaveDays.has(day)) continue;
      
      // Skip if attendance was marked
      if (attendedDays.has(day)) continue;
      
      // Working day with no attendance = absent
      absent++;
    }
    return absent;
  })();
  
  // Count approved leaves (real-time count from leave requests)
  const leaveCount = leaveRequests.filter(l => l.status === 'approved').length;
  
  const weeklyOffCount = attendance.filter(a => {
    const status = getAttendanceStatus(a);
    return status === 'WO' || status === 'H';
  }).length;
  
  const pendingLeaves = leaveRequests.filter(l => l.status === 'pending').length;
  
  // ===== ACCURATE ABSENT CALCULATION HELPER =====
  // Counts working days where an employee had NO attendance in a given month
  // Respects joiningDate — only counts absences from joining date onwards
  const calculateEmployeeAbsentDays = useCallback((employeeId, targetMonth, targetYear, empAttendanceData, employeeJoiningDate) => {
    const now = new Date();
    // Future month — no absent days to count yet
    const isFutureMonth = targetYear > now.getFullYear() ||
      (targetYear === now.getFullYear() && targetMonth > now.getMonth());
    if (isFutureMonth) return 0;

    const isCurrentMonth = now.getMonth() === targetMonth && now.getFullYear() === targetYear;
    // For current month, count up to yesterday (exclude today — day isn't over)
    // For past months, count all days including the last day
    const lastDay = isCurrentMonth ? now.getDate() - 1 : new Date(targetYear, targetMonth + 1, 0).getDate();

    // Determine start day based on joining date
    let startDay = 1;
    if (employeeJoiningDate) {
      const jd = new Date(employeeJoiningDate);
      if (jd.getFullYear() === targetYear && jd.getMonth() === targetMonth) {
        startDay = jd.getDate();
      } else if (jd.getFullYear() > targetYear || (jd.getFullYear() === targetYear && jd.getMonth() > targetMonth)) {
        // Employee hadn't joined yet in this month
        return 0;
      }
    }

    // Build Set of days with any attendance record
    const attendedDays = new Set();
    empAttendanceData.forEach(a => {
      const d = new Date(a.date);
      if (d.getFullYear() === targetYear && d.getMonth() === targetMonth) {
        const status = getAttendanceStatus(a);
        if (status === 'P' || status === 'L' || status === 'WO' || status === 'H') attendedDays.add(d.getDate());
      }
    });

    // Build Set of holiday dates
    const holidayDays = new Set();
    (holidays || []).forEach(h => {
      const d = new Date(h.date);
      if (d.getFullYear() === targetYear && d.getMonth() === targetMonth) {
        holidayDays.add(d.getDate());
      }
    });

    // Build Set of approved leave dates
    const leaveDays = new Set();
    empAttendanceData.forEach(a => {
      const d = new Date(a.date);
      if (d.getFullYear() === targetYear && d.getMonth() === targetMonth) {
        const status = getAttendanceStatus(a);
        if (status === 'L') leaveDays.add(d.getDate());
      }
    });

    let absent = 0;
    for (let day = startDay; day <= lastDay; day++) {
      const date = new Date(targetYear, targetMonth, day);
      const dayOfWeek = date.getDay();
      // Skip only Monday (1 = weekly off)
      if (dayOfWeek === 1) continue;
      if (holidayDays.has(day)) continue;
      if (leaveDays.has(day)) continue;
      if (attendedDays.has(day)) continue;
      absent++;
    }
    return absent;
  }, [holidays]);

  // Check if today is a working day
  const isTodayWorkingDay = useCallback(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    if (dayOfWeek === 1) return false;
    const isHoliday = (holidays || []).some(h => new Date(h.date).toDateString() === now.toDateString());
    return !isHoliday;
  }, [holidays]);

  // Manager helper functions
  const getEmployeeAttendanceForDate = (employeeId, date) => {
    const record = teamAttendance.find(a => 
      String(a.employee) === String(employeeId) && 
      new Date(a.date).toDateString() === date.toDateString()
    );
    if (!record) return 'A';
    return getAttendanceStatus(record);
  };
  
  const downloadAttendanceReport = () => {
    const today = new Date();
    const month = format(selectedMonth, 'MMMM yyyy');
    
    // Create CSV content
    let csv = `Attendance Report - ${month}\n\n`;
    csv += 'Employee Name,Total Present,Total Absent,Total Leave,Weekly Off/Holiday\n';
    
    employees.filter(e => e.status !== 'inactive').forEach(emp => {
      const empAttendance = teamAttendance.filter(a => a.employee === emp._id);
      const present = empAttendance.filter(a => getAttendanceStatus(a) === 'P').length;
      const absent = calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), empAttendance, emp.joiningDate || emp.createdAt);
      const leave = empAttendance.filter(a => getAttendanceStatus(a) === 'L').length;
      const wo = empAttendance.filter(a => ['WO', 'H'].includes(getAttendanceStatus(a))).length;
      csv += `${emp.name || emp.email},${present},${absent},${leave},${wo}\n`;
    });
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${format(today, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openEmployeeTimeline = async (employee, monthDate = new Date()) => {
    setSelectedEmployeeTimeline(employee);
    setTimelineData(null);
    setTimelineLoading(true);
    try {
      const month = monthDate.getMonth() + 1;
      const year = monthDate.getFullYear();
      const res = await attendanceAPI.getEmployeeMonthlyTimeline(employee._id, month, year);
      setTimelineData(res.data);
    } catch (err) {
      console.error('Failed to load employee timeline:', err);
      alert(err?.response?.data?.message || 'Failed to load daily attendance timeline');
      setSelectedEmployeeTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  };

  const getTimelineBadgeClass = (status) => {
    if (status === 'PRESENT') return 'bg-green-100 text-green-800';
    if (status === 'ABSENT') return 'bg-red-100 text-red-800';
    if (status === 'LEAVE') return 'bg-blue-100 text-blue-800';
    if (status === 'WEEK_OFF') return 'bg-purple-100 text-purple-800';
    if (status === 'HOLIDAY') return 'bg-yellow-100 text-yellow-800';
    if (status === 'HALF_DAY') return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-700';
  };

  if (permissionsLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="hidden md:block"><Sidebar /></div>
        <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
          <Header user={user} newMessageCount={newMessageCount} resetNewMessageCount={resetNewMessageCount} />
          <main className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
            <div className="text-gray-600 text-sm">Loading permissions...
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!canViewAttendance) {
    return (
      <div className="flex h-screen bg-gray-50">
        <div className="hidden md:block"><Sidebar /></div>
        <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
          <Header user={user} newMessageCount={newMessageCount} resetNewMessageCount={resetNewMessageCount} />
          <main className="flex-1 flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 sm:px-8 py-6 rounded-lg text-center max-w-md mx-4 sm:mx-0">
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p>You do not have permission to view attendance.</p>
              <p className="text-sm mt-3 text-red-600">Contact an administrator if you need access.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      <div className="hidden md:block"><Sidebar /></div>
      <div className={`flex-1 flex flex-col overflow-hidden ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        <Header user={user} newMessageCount={newMessageCount} resetNewMessageCount={resetNewMessageCount} />
        <main className="flex-1 overflow-auto p-4 sm:p-8">
          {/* Today's Check-in/Check-out */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:space-x-8 justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-base font-semibold text-gray-700">Check-in:</span>
                <span className="text-lg font-bold text-green-700">{todayCheckIn}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-base font-semibold text-gray-700">Check-out:</span>
                <span className="text-lg font-bold text-orange-600">{todayCheckOut}</span>
              </div>
            </div>
            {user && user.role === 'MANAGER' && (
              <div className="flex justify-end mt-3 md:mt-0">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded text-xs font-semibold shadow hover:bg-blue-700"
                  onClick={() => setShowHolidayForm((v) => !v)}
                >
                  {showHolidayForm ? 'Close Holiday Form' : '+ Add Holiday'}
                </button>
              </div>
            )}
          </div>
          
          {/* Holiday Form - Show right after the button */}
          {user && user.role === 'MANAGER' && showHolidayForm && (
            <div className="mb-6">
              <HolidayForm onHolidayAdded={fetchHolidays} />
            </div>
          )}
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-blue-800 tracking-tight">Attendance & Leave</h1>
          </div>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Present (P)</p>
                    <p className="text-2xl font-extrabold text-green-600">{presentCount}</p>
                </div>
                <FiCheckCircle size={28} className="text-green-500" />
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Absent (A)</p>
                    <p className="text-2xl font-extrabold text-red-600">{absentCount}</p>
                </div>
                <FiAlertCircle size={28} className="text-red-500" />
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Approved Leaves</p>
                    <p className="text-2xl font-extrabold text-blue-600">{leaveCount}</p>
                </div>
                <FiClock size={28} className="text-blue-500" />
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">WO/Holiday</p>
                    <p className="text-2xl font-extrabold text-purple-600">{weeklyOffCount}</p>
                </div>
                <span className="text-2xl">🏖️</span>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Pending Leaves</p>
                    <p className="text-2xl font-extrabold text-orange-600">{pendingLeaves}</p>
                </div>
                <FiClock size={28} className="text-orange-500" />
              </div>
            </div>
          </div>
          
          {/* Manager Team Attendance View */}
          {user && canViewTeamAttendance && (
            <div className="mb-8 bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-800">Team Attendance</h2>
                <div className="flex gap-3">
                  <button
                    onClick={() => setViewMode(viewMode === 'daily' ? 'monthly' : 'daily')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition"
                  >
                    {viewMode === 'daily' ? '📅 Monthly View' : '📆 Daily View'}
                  </button>
                  <button
                    onClick={downloadAttendanceReport}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition"
                  >
                    📥 Download Report
                  </button>
                </div>
              </div>
              
              {/* Daily View - Current View */}
              {viewMode === 'daily' && (
                <>
                  {/* Team Summary Cards */}
                  {(() => {
                    const activeEmployees = employees.filter(e => e.status !== 'inactive');
                    const activeCount = activeEmployees.length;
                    const presentToday = teamAttendance.filter(a => isToday(new Date(a.date)) && (a.checkIn || a.check_in)).length;
                    const leaveToday = teamAttendance.filter(a => isToday(new Date(a.date)) && getAttendanceStatus(a) === 'L').length;
                    const absentToday = Math.max(0, activeCount - presentToday - leaveToday);
                    return (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                      <p className="text-xs text-green-700 font-semibold mb-1">Total Team</p>
                      <p className="text-3xl font-extrabold text-green-800">{activeCount}</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                      <p className="text-xs text-blue-700 font-semibold mb-1">Present Today</p>
                      <p className="text-3xl font-extrabold text-blue-800">{presentToday}</p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                      <p className="text-xs text-red-700 font-semibold mb-1">Absent Today</p>
                      <p className="text-3xl font-extrabold text-red-800">{absentToday}</p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <p className="text-xs text-orange-700 font-semibold mb-1">On Leave</p>
                      <p className="text-3xl font-extrabold text-orange-800">{leaveToday}</p>
                    </div>
                  </div>
                    );
                  })()}
                  
                  {/* Employee-wise Attendance Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-blue-800">Employee</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Today Status</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Present</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Absent</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Leave</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Daily Records</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {employees.filter(e => e.status !== 'inactive').length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                              No team members found
                            </td>
                          </tr>
                        ) : (
                          employees.filter(e => e.status !== 'inactive').map(emp => {
                            const now = new Date();
                            // empAttendance scoped to current month so counts reset on month change
                            const empAttendance = teamAttendance.filter(a =>
                              String(a.employee) === String(emp._id) &&
                              isSameMonth(new Date(a.date), now)
                            );
                            const todayStatus = getEmployeeAttendanceForDate(emp._id, now);
                            const present = empAttendance.filter(a => getAttendanceStatus(a) === 'P').length;
                            const absent = calculateEmployeeAbsentDays(emp._id, now.getMonth(), now.getFullYear(), empAttendance, emp.joiningDate || emp.createdAt);
                            const leave = empAttendance.filter(a => getAttendanceStatus(a) === 'L').length;
                            
                            console.log(`👤 ${emp.name}: empAttendance=${empAttendance.length}, present=${present}, absent=${absent}, leave=${leave}`);
                            
                            return (
                              <tr key={emp._id} className="hover:bg-blue-50 transition">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-bold text-xs">
                                      {emp.name ? emp.name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <span className="font-semibold text-gray-800">{emp.name || emp.email}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                                    todayStatus === 'P' ? 'bg-green-100 text-green-800' :
                                    todayStatus === 'A' ? 'bg-red-100 text-red-800' :
                                    todayStatus === 'L' ? 'bg-blue-100 text-blue-800' :
                                    todayStatus === 'WO' ? 'bg-purple-100 text-purple-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {todayStatus === 'P' ? 'Present' :
                                     todayStatus === 'A' ? 'Absent' :
                                     todayStatus === 'L' ? 'Leave' :
                                     todayStatus === 'WO' ? 'Weekly Off' : 'Holiday'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="text-green-700 font-bold">{present}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`font-bold ${absent > 5 ? 'text-red-700' : 'text-red-600'}`}>{absent}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="text-blue-700 font-bold">{leave}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => openEmployeeTimeline(emp, new Date())}
                                    className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition"
                                  >
                                    View Daily
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              
              {/* Monthly View - Detailed Monthly Summary */}
              {viewMode === 'monthly' && (
                <>
                  {/* Month Selector */}
                  <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-200">
                    <button
                      onClick={() => {
                        const prevMonth = new Date(selectedMonth);
                        prevMonth.setMonth(prevMonth.getMonth() - 1);
                        setSelectedMonth(prevMonth);
                      }}
                      className="px-4 py-2 bg-white rounded-lg text-indigo-700 font-semibold hover:bg-indigo-100 transition border border-indigo-200"
                    >
                      ← Previous
                    </button>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-indigo-800">
                        {format(selectedMonth, 'MMMM yyyy')}
                      </h3>
                      <p className="text-xs text-indigo-600 mt-1">Monthly Attendance Summary</p>
                    </div>
                    <button
                      onClick={() => {
                        const nextMonth = new Date(selectedMonth);
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        setSelectedMonth(nextMonth);
                      }}
                      className="px-4 py-2 bg-white rounded-lg text-indigo-700 font-semibold hover:bg-indigo-100 transition border border-indigo-200"
                    >
                      Next →
                    </button>
                  </div>
                  
                  {/* Monthly Statistics Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                      <p className="text-xs text-purple-700 font-semibold mb-1">Total Employees</p>
                      <p className="text-3xl font-extrabold text-purple-800">{employees.filter(e => e.status !== 'inactive').length}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                      <p className="text-xs text-green-700 font-semibold mb-1">Avg Present/Day</p>
                      <p className="text-3xl font-extrabold text-green-800">
                        {(() => {
                          const monthAttendance = teamAttendance.filter(a => {
                            const attDate = new Date(a.date);
                            return attDate.getMonth() === selectedMonth.getMonth() && 
                                   attDate.getFullYear() === selectedMonth.getFullYear();
                          });
                          const presentDays = monthAttendance.filter(a => getAttendanceStatus(a) === 'P').length;
                          const workingDays = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
                          return employees.length > 0 ? Math.round(presentDays / workingDays) : 0;
                        })()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                      <p className="text-xs text-red-700 font-semibold mb-1">Total Absences</p>
                      <p className="text-3xl font-extrabold text-red-800">
                        {(() => {
                          let totalAbsent = 0;
                          employees.filter(e => e.status !== 'inactive').forEach(emp => {
                            const empAtt = teamAttendance.filter(a => {
                              const attDate = new Date(a.date);
                              return attDate.getMonth() === selectedMonth.getMonth() &&
                                     attDate.getFullYear() === selectedMonth.getFullYear() &&
                                     String(a.employee) === String(emp._id);
                            });
                            totalAbsent += calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), empAtt, emp.joiningDate || emp.createdAt);
                          });
                          return totalAbsent;
                        })()}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                      <p className="text-xs text-orange-700 font-semibold mb-1">Total Leaves</p>
                      <p className="text-3xl font-extrabold text-orange-800">
                        {teamAttendance.filter(a => {
                          const attDate = new Date(a.date);
                          return attDate.getMonth() === selectedMonth.getMonth() && 
                                 attDate.getFullYear() === selectedMonth.getFullYear() &&
                                 getAttendanceStatus(a) === 'L';
                        }).length}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                      <p className="text-xs text-yellow-700 font-semibold mb-1">Low Attendance (&lt;75%)</p>
                      <p className="text-3xl font-extrabold text-yellow-800">
                        {employees.filter(emp => {
                          if (emp.status === 'inactive') return false;
                          const monthAtt = teamAttendance.filter(a => {
                            const attDate = new Date(a.date);
                            return attDate.getMonth() === selectedMonth.getMonth() &&
                                   attDate.getFullYear() === selectedMonth.getFullYear() &&
                                   String(a.employee) === String(emp._id);
                          });
                          const absent = calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), monthAtt, emp.joiningDate || emp.createdAt);
                          const wo = monthAtt.filter(a => getAttendanceStatus(a) === 'WO').length;
                          const totalDays = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
                          const workingDays = totalDays - wo;
                          const percent = workingDays > 0 ? Math.max(0, Math.round(100 - (absent / workingDays) * 100)) : 100;
                          return percent < 75;
                        }).length}
                      </p>
                    </div>
                  </div>
                  
                  {/* Detailed Monthly Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-indigo-800 sticky left-0 bg-indigo-50">Employee</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-indigo-800">Total Days</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-green-800">Present</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-red-800">Absent</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Leave</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-purple-800">Weekly Off</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-yellow-800">Attendance %</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-indigo-800">Daily Records</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {employees.filter(e => e.status !== 'inactive').length === 0 ? (
                          <tr>
                            <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                              No team members found
                            </td>
                          </tr>
                        ) : (
                          employees.filter(e => e.status !== 'inactive').map(emp => {
                            // Filter attendance for selected month
                            const monthAttendance = teamAttendance.filter(a => {
                              const attDate = new Date(a.date);
                              return attDate.getMonth() === selectedMonth.getMonth() && 
                                     attDate.getFullYear() === selectedMonth.getFullYear() &&
                                     String(a.employee) === String(emp._id);
                            });
                            
                            const present = monthAttendance.filter(a => getAttendanceStatus(a) === 'P').length;
                            const absent = calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), monthAttendance, emp.joiningDate || emp.createdAt);
                            const leave = monthAttendance.filter(a => getAttendanceStatus(a) === 'L').length;
                            const weeklyOff = monthAttendance.filter(a => getAttendanceStatus(a) === 'WO').length;
                            const totalDays = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
                            const workingDays = totalDays - weeklyOff;
                            // Calculate attendance as 100% minus absent percentage
                            const attendancePercent = workingDays > 0 ? Math.max(0, Math.round(100 - (absent / workingDays) * 100)) : 100;
                            
                            return (
                              <tr key={emp._id} className="hover:bg-indigo-50 transition">
                                <td className="px-4 py-3 sticky left-0 bg-white">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                                      {emp.name ? emp.name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div>
                                      <span className="font-semibold text-gray-800 block">{emp.name || emp.email}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="font-semibold text-gray-700">{totalDays}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full font-bold">
                                    {present}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-3 py-1 rounded-full font-bold ${
                                    absent > 5 ? 'bg-red-200 text-red-900' : 'bg-red-100 text-red-800'
                                  }`}>
                                    {absent}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-bold">
                                    {leave}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full font-bold">
                                    {weeklyOff}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <div className="w-24 bg-gray-200 rounded-full h-2">
                                      <div 
                                        className={`h-2 rounded-full ${
                                          attendancePercent >= 75 ? 'bg-green-600' : 'bg-red-600'
                                        }`}
                                        style={{ width: `${attendancePercent}%` }}
                                      ></div>
                                    </div>
                                    <span className={`font-bold text-sm ${
                                      attendancePercent >= 75 ? 'text-green-700' : 'text-red-700'
                                    }`}>
                                      {attendancePercent}%
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => openEmployeeTimeline(emp, selectedMonth)}
                                    className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition"
                                  >
                                    View Daily
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Monthly Summary Footer */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-indigo-700 font-semibold mb-1">📊 Summary for {format(selectedMonth, 'MMMM yyyy')}</p>
                        <p className="text-xs text-indigo-600">Total Records: {teamAttendance.filter(a => {
                          const attDate = new Date(a.date);
                          return attDate.getMonth() === selectedMonth.getMonth() && 
                                 attDate.getFullYear() === selectedMonth.getFullYear();
                        }).length}</p>
                      </div>
                      <div>
                        <p className="text-green-700 font-semibold mb-1">✅ Best Performer</p>
                        <p className="text-xs text-green-600">
                          {(() => {
                            let best = null;
                            let maxPercent = 0;
                            employees.filter(e => e.status !== 'inactive').forEach(emp => {
                              const monthAtt = teamAttendance.filter(a => {
                                const attDate = new Date(a.date);
                                return attDate.getMonth() === selectedMonth.getMonth() &&
                                       attDate.getFullYear() === selectedMonth.getFullYear() &&
                                       String(a.employee) === String(emp._id);
                              });
                              const absent = calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), monthAtt, emp.joiningDate || emp.createdAt);
                              const wo = monthAtt.filter(a => getAttendanceStatus(a) === 'WO').length;
                              const totalDays = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
                              const workingDays = totalDays - wo;
                              const percent = workingDays > 0 ? Math.max(0, Math.round(100 - (absent / workingDays) * 100)) : 100;
                              if (percent > maxPercent) {
                                maxPercent = percent;
                                best = emp.name || emp.email;
                              }
                            });
                            return best ? `${best} (${maxPercent}%)` : 'N/A';
                          })()}
                        </p>
                      </div>
                      <div>
                        <p className="text-red-700 font-semibold mb-1">⚠ Requires Attention</p>
                        <p className="text-xs text-red-600">
                          {(() => {
                            const lowAttendanceCount = employees.filter(emp => emp.status !== 'inactive').filter(emp => {
                              const monthAtt = teamAttendance.filter(a => {
                                const attDate = new Date(a.date);
                                return attDate.getMonth() === selectedMonth.getMonth() &&
                                       attDate.getFullYear() === selectedMonth.getFullYear() &&
                                       String(a.employee) === String(emp._id);
                              });
                              const absent = calculateEmployeeAbsentDays(emp._id, selectedMonth.getMonth(), selectedMonth.getFullYear(), monthAtt, emp.joiningDate || emp.createdAt);
                              const wo = monthAtt.filter(a => getAttendanceStatus(a) === 'WO').length;
                              const totalDays = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
                              const workingDays = totalDays - wo;
                              const percent = workingDays > 0 ? Math.max(0, Math.round(100 - (absent / workingDays) * 100)) : 100;
                              return percent < 75;
                            }).length;
                            return `${lowAttendanceCount} employee${lowAttendanceCount !== 1 ? 's' : ''}`;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
            {/* Attendance summary card removed as requested */}
          {/* Clock & Mark Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {/* Left - Clock & Marking */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                <LiveClock />
              </div>
              {/* Location Status & Mark Button */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                <h3 className="text-base font-bold text-blue-800 mb-4">Mark Attendance</h3>
                
                {/* Geo-fence Status Display */}
                {isCheckingLocation ? (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-sm font-semibold text-blue-800">Checking Location...</p>
                      <p className="text-sm text-blue-700 mt-1">Verifying your position</p>
                    </div>
                  </div>
                ) : locationPermissionDenied ? (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <FiAlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Location Access Required</p>
                      <p className="text-sm text-red-700 mt-1">{locationError || 'Please enable location services to mark attendance'}</p>
                    </div>
                  </div>
                ) : !isWithinOfficeGeofence ? (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                    <FiAlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-yellow-800">Outside Office Premises</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {locationError || 'You must be within office premises to mark attendance'}
                      </p>
                      {distanceFromOffice && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Distance from office: {distanceFromOffice}m (Max: {ALLOWED_RADIUS_METERS}m)
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <FiCheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-800">✓ Within Office Premises</p>
                      <p className="text-sm text-green-700 mt-1">
                        You are within {ALLOWED_RADIUS_METERS}m radius
                      </p>
                      {distanceFromOffice !== null && (
                        <p className="text-xs text-green-600 mt-1">
                          Distance from office: {distanceFromOffice}m
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => {
                    checkUserLocation();
                    if (checkedInToday && isAfterNoon && !checkedOutToday) {
                      handleCheckOut();
                    } else {
                      handleMark();
                    }
                  }}
                  disabled={
                    loading || !isWithinOfficeGeofence || locationPermissionDenied || isCheckingLocation ||
                    (!checkedInToday ? false : // Not checked in yet → enabled
                      checkedOutToday ? true : // Already checked out → disabled
                        !isAfterNoon // Checked in but before 12PM → disabled
                    )
                  }
                  className={`w-full py-3 px-4 rounded-lg font-bold text-white text-base transition-all duration-150 ${
                    checkedOutToday
                      ? 'bg-gray-400 cursor-not-allowed'
                      : (checkedInToday && !isAfterNoon)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : (checkedInToday && isAfterNoon && !checkedOutToday)
                      ? 'bg-orange-500 hover:bg-orange-600 active:scale-95'
                      : (!isWithinOfficeGeofence || locationPermissionDenied || isCheckingLocation)
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 active:scale-95'
                  }`}
                >
                  {checkedOutToday
                    ? '✓ Done for Today'
                    : checkedInToday && isAfterNoon
                    ? '🏠 Check Out'
                    : checkedInToday
                    ? '✓ Checked In (Check-out after 12 PM)'
                    : isCheckingLocation
                    ? 'Checking Location...'
                    : '📍 Mark Attendance'
                  }
                </button>
              </div>
            </div>
            {/* Right - Calendar with Holidays */}
            <div>
              <CalendarWidget tasks={[]} holidays={holidays} compact={true} />
            </div>
          </div>
          {/* Employee Monthly View */}
          {selectedEmployeeTimeline && (
            <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4">
              <div className="w-full max-w-5xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-indigo-100 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                  <div>
                    <h3 className="text-lg font-bold">Daily Punch-In Timeline</h3>
                    <p className="text-xs text-indigo-100">
                      {selectedEmployeeTimeline.name || selectedEmployeeTimeline.email}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedEmployeeTimeline(null)}
                    className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 text-sm font-semibold"
                  >
                    Close
                  </button>
                </div>

                <div className="p-5 overflow-auto max-h-[calc(85vh-72px)]">
                  {timelineLoading ? (
                    <div className="text-sm text-gray-600">Loading daily records...</div>
                  ) : timelineData ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs text-green-700">Present</p>
                          <p className="text-xl font-bold text-green-800">{timelineData.summary?.present || 0}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-xs text-red-700">Absent</p>
                          <p className="text-xl font-bold text-red-800">{timelineData.summary?.absent || 0}</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-700">Leave</p>
                          <p className="text-xl font-bold text-blue-800">{timelineData.summary?.leave || 0}</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <p className="text-xs text-purple-700">Week Off</p>
                          <p className="text-xl font-bold text-purple-800">{timelineData.summary?.weekOff || 0}</p>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-xs text-yellow-700">Holiday</p>
                          <p className="text-xl font-bold text-yellow-800">{timelineData.summary?.holiday || 0}</p>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-700">Month</p>
                          <p className="text-base font-bold text-gray-800">{timelineData.month}/{timelineData.year}</p>
                        </div>
                      </div>

                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Day</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Punch In</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Punch Out</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(timelineData.days || []).map((row) => (
                              <tr key={row.date}>
                                <td className="px-3 py-2 text-gray-800">{format(new Date(row.date), 'dd MMM yyyy')}</td>
                                <td className="px-3 py-2 text-gray-600">{row.dayLabel}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getTimelineBadgeClass(row.status)}`}>
                                    {row.status === 'WEEK_OFF' ? 'WEEK OFF' : row.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center text-gray-700">{row.punchInTime || '-'}</td>
                                <td className="px-3 py-2 text-center text-gray-700">{row.punchOutTime || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-600">No timeline data found.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Employee Monthly View */}
          {user && user.role === 'EMPLOYEE' && (
            <div className="mb-8 bg-white rounded-2xl shadow-xl border border-blue-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-blue-800">My Attendance History</h2>
                <button
                  onClick={() => setEmployeeViewMode(employeeViewMode === 'current' ? 'history' : 'current')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition"
                >
                  {employeeViewMode === 'current' ? '📅 View Previous Months' : '📆 Current Month'}
                </button>
              </div>
              
              {employeeViewMode === 'history' && (
                <>
                  {/* Month Selector */}
                  <div className="mb-6 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-200">
                    <button
                      onClick={() => {
                        const prevMonth = new Date(employeeSelectedMonth);
                        prevMonth.setMonth(prevMonth.getMonth() - 1);
                        setEmployeeSelectedMonth(prevMonth);
                      }}
                      className="px-4 py-2 bg-white rounded-lg text-indigo-700 font-semibold hover:bg-indigo-100 transition border border-indigo-200"
                    >
                      ← Previous
                    </button>
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-indigo-800">
                        {format(employeeSelectedMonth, 'MMMM yyyy')}
                      </h3>
                      <p className="text-xs text-indigo-600 mt-1">My Attendance Summary</p>
                    </div>
                    <button
                      onClick={() => {
                        const nextMonth = new Date(employeeSelectedMonth);
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        setEmployeeSelectedMonth(nextMonth);
                      }}
                      className="px-4 py-2 bg-white rounded-lg text-indigo-700 font-semibold hover:bg-indigo-100 transition border border-indigo-200"
                    >
                      Next →
                    </button>
                  </div>
                  
                  {/* Monthly Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    {(() => {
                      const monthRecords = attendance.filter(a => {
                        const attDate = new Date(a.date);
                        return attDate.getMonth() === employeeSelectedMonth.getMonth() && 
                               attDate.getFullYear() === employeeSelectedMonth.getFullYear();
                      });
                      const monthPresent = monthRecords.filter(a => getAttendanceStatus(a) === 'P').length;
                      const monthAbsent = monthRecords.filter(a => getAttendanceStatus(a) === 'A').length;
                      const monthLeave = monthRecords.filter(a => getAttendanceStatus(a) === 'L').length;
                      const monthWO = monthRecords.filter(a => ['WO', 'H'].includes(getAttendanceStatus(a))).length;
                      
                      return (
                        <>
                          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                            <p className="text-xs text-green-700 font-semibold mb-1">Present Days</p>
                            <p className="text-3xl font-extrabold text-green-800">{monthPresent}</p>
                          </div>
                          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                            <p className="text-xs text-red-700 font-semibold mb-1">Absent Days</p>
                            <p className="text-3xl font-extrabold text-red-800">{monthAbsent}</p>
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                            <p className="text-xs text-blue-700 font-semibold mb-1">Leave Days</p>
                            <p className="text-3xl font-extrabold text-blue-800">{monthLeave}</p>
                          </div>
                          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                            <p className="text-xs text-purple-700 font-semibold mb-1">Weekly Off/Holiday</p>
                            <p className="text-3xl font-extrabold text-purple-800">{monthWO}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Monthly Attendance Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-blue-800">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-blue-800">Day</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Check In</th>
                          <th className="px-4 py-3 text-center text-xs font-bold text-blue-800">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(() => {
                          const monthRecords = attendance.filter(a => {
                            const attDate = new Date(a.date);
                            return attDate.getMonth() === employeeSelectedMonth.getMonth() && 
                                   attDate.getFullYear() === employeeSelectedMonth.getFullYear();
                          }).sort((a, b) => new Date(b.date) - new Date(a.date));
                          
                          if (monthRecords.length === 0) {
                            return (
                              <tr>
                                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                                  No attendance records for this month
                                </td>
                              </tr>
                            );
                          }
                          
                          return monthRecords.map((record) => {
                            const status = getAttendanceStatus(record);
                            const recordDate = new Date(record.date);
                            const dayName = format(recordDate, 'EEEE');
                            
                            return (
                              <tr key={record.id || record._id} className="hover:bg-blue-50 transition">
                                <td className="px-4 py-3">
                                  <span className="font-bold text-blue-900">
                                    {format(recordDate, 'MMM dd, yyyy')}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-gray-700 font-medium">{dayName}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className="text-blue-700 font-semibold">
                                    {(record.check_in && !isNaN(new Date(record.check_in))) 
                                      ? format(new Date(record.check_in), 'HH:mm') 
                                      : '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                                    status === 'P' ? 'bg-green-100 text-green-800' :
                                    status === 'A' ? 'bg-red-100 text-red-800' :
                                    status === 'L' ? 'bg-blue-100 text-blue-800' :
                                    status === 'WO' ? 'bg-purple-100 text-purple-800' :
                                    status === 'H' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {status === 'P' ? 'Present' :
                                     status === 'A' ? 'Absent' :
                                     status === 'L' ? 'Leave' :
                                     status === 'WO' ? 'Weekly Off' :
                                     status === 'H' ? 'Holiday' : status}
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* History & Leave Requests */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Attendance History */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-blue-100">
              <div className="border-b p-6 flex justify-between items-center">
                <h2 className="text-lg font-bold text-blue-800">Recent Attendance</h2>
                <a href="#" className="text-xs text-blue-600 hover:text-blue-700">View All</a>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-blue-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-blue-700">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-blue-700">Day</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-blue-700">Check In</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-blue-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {attendance.filter(record => isSameMonth(new Date(record.date), new Date())).length === 0 ? (
                      <tr>
                        <td colSpan="4" className="px-4 py-6 text-center text-xs text-gray-500">
                          No attendance records for this month
                        </td>
                      </tr>
                    ) : (
                      attendance
                        .filter(record => isSameMonth(new Date(record.date), new Date()))
                        .slice(0, 10)
                        .map((record) => {
                        const status = getAttendanceStatus(record);
                        const recordDate = new Date(record.date);
                        const dayName = format(recordDate, 'EEE');
                        return (
                        <tr key={record.id} className="hover:bg-blue-50">
                          <td className="px-4 py-3 text-xs text-blue-900 font-bold">
                            {(record.date && !isNaN(new Date(record.date))) ? format(new Date(record.date), 'MMM dd') : '-'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700 font-medium">
                            {dayName}
                          </td>
                          <td className="px-4 py-3 text-xs text-blue-700">
                            {(record.check_in && !isNaN(new Date(record.check_in))) ? format(new Date(record.check_in), 'HH:mm') : '-'}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                              status === 'P' ? 'bg-green-100 text-green-800' :
                              status === 'A' ? 'bg-red-100 text-red-800' :
                              status === 'L' ? 'bg-blue-100 text-blue-800' :
                              status === 'WO' ? 'bg-purple-100 text-purple-800' :
                              status === 'H' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {status === 'P' ? 'Present' :
                               status === 'A' ? 'Absent' :
                               status === 'L' ? 'Leave' :
                               status === 'WO' ? 'Weekly Off' :
                               status === 'H' ? 'Holiday' : status}
                            </span>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Leave Requests */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100">
              <div className="border-b p-6 flex justify-between items-center">
                <h2 className="text-lg font-bold text-blue-800">Leave Requests</h2>
                <button
                  onClick={() => setShowLeaveForm(!showLeaveForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-all duration-150"
                >
                  + New
                </button>
              </div>
              {showLeaveForm && (
                <form onSubmit={handleLeaveSubmit} className="p-6 border-b bg-blue-50 space-y-4 rounded-b-2xl">
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="w-full px-3 py-2 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="w-full px-3 py-2 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-2">Type</label>
                    <select
                      value={formData.leave_type}
                      onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                      className="w-full px-3 py-2 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="sick">Sick Leave</option>
                      <option value="casual">Casual Leave</option>
                      <option value="emergency">Emergency</option>
                      <option value="wfh">Work From Home</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-blue-700 mb-2">Reason</label>
                    <textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({...formData, reason: e.target.value})}
                      className="w-full px-3 py-2 text-xs border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg text-xs font-bold shadow transition-all duration-150"
                  >
                    Submit
                  </button>
                </form>
              )}
              <div className="divide-y max-h-64 overflow-y-auto">
                {leaveRequests.length === 0 ? (
                  <div className="p-6 text-center text-xs text-gray-500">No leave requests</div>
                ) : (
                  leaveRequests.map((req) => (
                    <div key={req.id} className="p-4 hover:bg-blue-50 rounded-xl">
                      <p className="font-bold text-xs text-blue-800">{req.leave_type}</p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        {(req.start_date && !isNaN(new Date(req.start_date))) ? format(new Date(req.start_date), 'MMM dd') : '-'} - {(req.end_date && !isNaN(new Date(req.end_date))) ? format(new Date(req.end_date), 'MMM dd') : '-'}
                      </p>
                      <p className="text-xs text-blue-600 mt-1 line-clamp-2">{req.reason}</p>
                      <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold rounded-lg ${
                        req.status === 'approved' ? 'bg-green-100 text-green-800' :
                        req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {req.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
